import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { logout, tokenReceived } from '../features/auth/authSlice';
import { refrescarSesion } from './refrescarSesion';
import { RootState } from '../redux/store';
import { settings } from '../settings';

import type {
    AvisoObraItem,
    AvisoObraListParams,
    AvisoObraListResponse,
    AvisoObraDetailResponse,
    TipoObraItem,
    TipoResolucionItem,
    CreateAvisoObraDTO,
    ActividadEspecialItem
} from './../pages/AvisoObra/consts/avisoObraTypes';
import {
    transformAvisosResponse,
    transformAvisoDetailResponse,
    transformTiposObraResponse,
    transformTiposResolucionResponse,
    transformActividadesEspecialesResponse
} from './../pages/AvisoObra/consts/avisoObraTransform';

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
        return headers;
    }
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result: any = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        const refreshResult = await refrescarSesion(baseQuery, api, extraOptions);

        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

export const avisoObraApi = createApi({
    reducerPath: 'avisoObra',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['AvisoObra', 'TiposObra', 'TiposResolucion'],
    endpoints: (builder) => ({
        createAvisoObra: builder.mutation<{ data: number }, CreateAvisoObraDTO>({
            query: (body) => ({
                url: '/aviso-obra',
                method: 'POST',
                body
            })
        }),

        getAvisosObra: builder.query<AvisoObraItem[], AvisoObraListParams | void>({
            query: (params: any) => ({
                url: `/aviso-obra`,
                method: 'GET',
                params
            }),
            providesTags: ['AvisoObra'],
            transformResponse: (response: AvisoObraListResponse) =>
                transformAvisosResponse(response),
            keepUnusedDataFor: 5
        }),

        getAvisoObraById: builder.query<AvisoObraItem, number>({
            query: (idAvisoObra) => ({
                url: `/aviso-obra/aviso/${idAvisoObra}`,
                method: 'GET'
            }),
            providesTags: (_res, _err, id) => [{ type: 'AvisoObra', id }],
            transformResponse: (response: AvisoObraDetailResponse) =>
                transformAvisoDetailResponse(response),
            keepUnusedDataFor: 5
        }),

        getTiposObra: builder.query<TipoObraItem[], void>({
            query: () => ({
                url: `/aviso-obra/tiposObra`,
                method: 'GET'
            }),
            providesTags: ['TiposObra'],
            transformResponse: (response: { data: TipoObraItem[] } | any) =>
                transformTiposObraResponse(response),
            keepUnusedDataFor: 60
        }),

        getTiposResolucion: builder.query<TipoResolucionItem[], void>({
            query: () => ({
                url: `/aviso-obra/tipos-resolucion`,
                method: 'GET'
            }),
            providesTags: ['TiposResolucion'],
            transformResponse: (response: any) => transformTiposResolucionResponse(response),
            keepUnusedDataFor: 300
        }),
        getTiposActividadesEspeciales: builder.query<ActividadEspecialItem[], void>({
            query: () => ({
                url: `/aviso-obra/actividades-especiales`,
                method: 'GET'
            }),
            providesTags: ['TiposResolucion'],
            transformResponse: (response: any) => transformActividadesEspecialesResponse(response),
            keepUnusedDataFor: 300
        }),
        deregisterAvisoObra: builder.mutation({
            query: ({
                avisoDeObraID,
                body
            }: {
                avisoDeObraID: number;
                body: { observacion: string };
            }) => ({
                url: `aviso-obra/${avisoDeObraID}`,
                method: 'DELETE',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AvisoObra'])
        }),
        suspendAvisoObra: builder.mutation({
            query: ({
                avisoDeObraID,
                body
            }: {
                avisoDeObraID: number;
                body: { fechaSuspencion: Date; motivo: string };
            }) => ({
                url: `aviso-obra/${avisoDeObraID}/suspension`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AvisoObra'])
        }),
        extendAvisoObra: builder.mutation({
            query: ({
                avisoDeObraID,
                body
            }: {
                avisoDeObraID: number;
                body: { fechaExtension: Date; observacion: string };
            }) => ({
                url: `aviso-obra/${avisoDeObraID}/extension`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AvisoObra'])
        }),
        restartAvisoObra: builder.mutation({
            query: ({
                avisoDeObraID,
                body
            }: {
                avisoDeObraID: number;
                body: { fechaReinicio: Date; fechaFin: string };
            }) => ({
                url: `aviso-obra/${avisoDeObraID}/reinicio`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AvisoObra'])
        }),
        revisionAvisoObra: builder.mutation<void, number>({
            query: (idAvisoObra) => ({
                url: `/aviso-obra/${idAvisoObra}/revision`,
                method: 'PATCH'
            }),
            invalidatesTags: (_res, _err, idAvisoObra) => [{ type: 'AvisoObra', id: idAvisoObra }]
        }),
        updateAvisoObra: builder.mutation<void, { idAvisoObra: number; body: any }>({
            query: ({ idAvisoObra, body }) => {
                const isMultipart = body instanceof FormData;
                return {
                    url: `/aviso-obra/${idAvisoObra}`,
                    method: 'PATCH',
                    body,
                    headers: isMultipart ? {} : { 'Content-Type': 'application/json' }
                };
            },
            invalidatesTags: (_, error) => (error ? [] : ['AvisoObra'])
        }),
        generateNoObraPdf: builder.mutation<{ data: string }, number>({
            query: (idAvisoObra) => ({
                url: `/aviso-obra/aviso/${idAvisoObra}/pdf/no-obra`,
                method: 'POST'
            })
        }),
        downloadAvisoObraPdf: builder.mutation<{ data: string }, number>({
            query: (idAvisoObra) => ({
                url: `/aviso-obra/aviso/${idAvisoObra}/pdf/alta`,
                method: 'POST'
            })
        }),
        generateConstanciaPdf: builder.mutation<
            { data: string },
            {
                idAvisoObra: number;
                body?: {
                    aprobar?: boolean;
                    rechazar?: boolean;
                    observaciones?: string;
                    motivoRechazo?: string;
                };
            }
        >({
            query: ({ idAvisoObra, body }) => ({
                url: `/aviso-obra/aviso/${idAvisoObra}/pdf/constancia`,
                method: 'POST',
                body
            })
        })
    })
});

export const {
    useCreateAvisoObraMutation,
    useGetAvisosObraQuery,
    useLazyGetAvisosObraQuery,
    useGetAvisoObraByIdQuery,
    useLazyGetAvisoObraByIdQuery,
    useGetTiposObraQuery,
    useLazyGetTiposObraQuery,
    useGetTiposResolucionQuery,
    useLazyGetTiposResolucionQuery,
    useGetTiposActividadesEspecialesQuery,
    useLazyGetTiposActividadesEspecialesQuery,
    useDeregisterAvisoObraMutation,
    useSuspendAvisoObraMutation,
    useRestartAvisoObraMutation,
    useExtendAvisoObraMutation,
    useUpdateAvisoObraMutation,
    useRevisionAvisoObraMutation,
    useDownloadAvisoObraPdfMutation,
    useGenerateNoObraPdfMutation,
    useGenerateConstanciaPdfMutation
} = avisoObraApi;
